import { ListActivityAPIQueryParams } from '@goldcast/api/backend';
import { ContentMetadataStoreFactoryType } from '../stores/contentMetadataStoreFactory';
import type { Clip } from '../types/clip-types';
import { Word } from '../types/transcript-types';
import { getAlignedRecordings } from '../utils/alignedRecordings';
import {
  isClipFromUploads,
  isClipPreRecordedEvent,
  getClipBroadcastId,
  doesClipWorkOnAlignedStreams,
  isLiveEventRemotionWithPreviewAndAlignedStream
} from '../utils/clipUtils';
import { getSpeakersPayloadParams, formSpeakerDetails, populateSpeakersWithSpeakingSlots } from '../utils/speakerUtils';
import { fetchAlignedRecordings } from './fetchAlignedRecordings';
import { fetchSpeakers } from './fetchSpeakers';
import { fetchTranscript } from './fetchTranscript';
import { BaseSpeakerAnalysisOptions, getSpeakersAnalysis } from './speakerAnalysisApi';

export const prefetchDetailsForRemotionConfig = async ({
  clip,
  isSharedPage,
  isSavedSearchPage,
  isSmartEventsPage,
  isFullVideoFaceRecFeatureFlag,
  token,
  areLayoutsEnabledForPreRecordedEvents,
  isScreenshareDetectionEnabled,
  onSpeakerAnalsysisFetchFail,
  contentMetadataStoreInstance,
  onErrorToast,
  onFullTranscript
}: {
  clip: Clip;
  isSharedPage: boolean;
  isSavedSearchPage: boolean;
  isSmartEventsPage: boolean;
  isFullVideoFaceRecFeatureFlag: boolean;
  token: string;
  areLayoutsEnabledForPreRecordedEvents: boolean;
  isScreenshareDetectionEnabled: boolean;
  onSpeakerAnalsysisFetchFail: (error: any) => void;
  contentMetadataStoreInstance: ContentMetadataStoreFactoryType;
  onErrorToast: (message: string) => void;
  onFullTranscript: (fullTranscript: Word[]) => void;
}) => {
  try {
    if (clip.asset_metadata && (isClipFromUploads(clip) || isClipPreRecordedEvent(clip))) {
      const contentId = getClipBroadcastId(clip)!;
      const isFullVideoFaceRecNotApplicable =
        (clip.content as { face_recognition_status?: string }).face_recognition_status === 'NOT_APPLICABLE';
      const fetchForFullClip = isFullVideoFaceRecFeatureFlag && !isFullVideoFaceRecNotApplicable;

      const baseOptions: BaseSpeakerAnalysisOptions = {
        clipId: clip.id,
        clip,
        isLayoutEnabledForPreRecordedEvents: areLayoutsEnabledForPreRecordedEvents,
        token,
        contentMetadataStoreInstance,
        isScreenshareDetectionEnabled: isScreenshareDetectionEnabled
      };
      await getSpeakersAnalysis(
        fetchForFullClip
          ? {
              ...baseOptions,
              contentId
            }
          : {
              ...baseOptions,
              start: clip.asset_metadata.start
            }
      );
    }
  } catch (error) {
    onSpeakerAnalsysisFetchFail(error);
  }

  if (
    doesClipWorkOnAlignedStreams(clip) &&
    !isSharedPage &&
    getAlignedRecordings({ broadcastId: getClipBroadcastId(clip), contentMetadataStoreInstance }).length === 0
  ) {
    const queryParams: ListActivityAPIQueryParams = isLiveEventRemotionWithPreviewAndAlignedStream(clip)
      ? { status: 'complete' }
      : {};
    await fetchAlignedRecordings({
      broadcast_id: clip.broadcast,
      queryParams,
      contentMetadataStoreInstance: contentMetadataStoreInstance,
      areLayoutsEnabledForPreRecordedEvents: areLayoutsEnabledForPreRecordedEvents
    });
  }

  if (isSavedSearchPage || isSmartEventsPage) {
    // for both smart events and saved search page, SessionDetails (Parent TranscriptContext) doesn't exist.
    const broadcastId = clip.broadcast;
    const eventId = clip.event;
    const cacheSuffix = Math.random().toString();
    const [transcriptResult, sessionSpeakers] = await Promise.allSettled([
      fetchTranscript({ cacheSuffix, event: eventId!, broadcast: broadcastId!, onError: onErrorToast }),
      fetchSpeakers({
        queryParams: getSpeakersPayloadParams(broadcastId!, !isClipFromUploads(clip)),
        onErrorToast
      })
    ]);

    // Extract values and handle potential failures
    const transcriptData =
      transcriptResult.status === 'fulfilled'
        ? transcriptResult.value
        : {
            transcript: [],
            fullTranscript: []
          };
    if (transcriptResult.status === 'fulfilled' && transcriptData.fullTranscript.length > 0) {
      onFullTranscript(transcriptData.fullTranscript);
    }
    const speakersData = sessionSpeakers.status === 'fulfilled' ? sessionSpeakers.value : {};

    const speakers = formSpeakerDetails(speakersData);
    const { speakers: speakersWithSpeakingSlots } = populateSpeakersWithSpeakingSlots({
      speakers,
      transcriptResult: transcriptData,
      broadcastId: clip.content.id,
      contentMetadataStoreInstance
    });
    contentMetadataStoreInstance.getState().updateSpeakerDetails({
      contentId: getClipBroadcastId(clip)!,
      speakerDetails: speakersWithSpeakingSlots,
      areLayoutsEnabledForPreRecordedEvents: areLayoutsEnabledForPreRecordedEvents
    });
  }
};
